import type { BuildDerived, BuildState } from '@/types';
import { getShaftDiameterInches } from '@/lib/calculations';
import { Card } from '@/components/ui/Card';

interface StabilityCardProps {
  state: BuildState;
  derived: BuildDerived;
}

/**
 * Shows BC, CP, and the static-margin (stability) computation alongside a
 * mini diagram with CG, CP, and the static margin distance highlighted.
 */
export function StabilityCard({ state, derived }: StabilityCardProps) {
  const L = state.length;
  const od = getShaftDiameterInches(state);
  const cg = derived.balancePoint;
  const cp = derived.centerOfPressure;
  const margin = cg - cp;
  const calibers = derived.stabilityCalibers;

  const VIEW_W = 360;
  const PAD = 18;
  const usable = VIEW_W - PAD * 2;
  const xAt = (inches: number) => PAD + (inches / L) * usable;

  return (
    <Card padding="lg">
      <h2 className="font-semibold mb-3">Stability &amp; Aerodynamics</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <Stat label="BC" value={derived.bc.toFixed(3)} hint="lb/in²" />
        <Stat label="CG" value={`${cg.toFixed(2)}"`} hint="from nock" />
        <Stat label="CP" value={`${cp.toFixed(2)}"`} hint="from nock" />
        <Stat label="Static margin" value={`${calibers.toFixed(0)} cal`} hint={`${margin.toFixed(2)}"`} highlight />
      </div>

      <svg viewBox={`0 0 ${VIEW_W} 90`} className="w-full h-auto">
        <line x1={PAD} y1={40} x2={VIEW_W - PAD} y2={40}
          stroke="#52525b" strokeWidth={3} strokeLinecap="round" />
        {/* CP marker */}
        <circle cx={xAt(cp)} cy={40} r={5} fill="#a78bfa" />
        <text x={xAt(cp)} y={62} textAnchor="middle" fontSize={9}
          fill="#a78bfa" fontFamily="JetBrains Mono">CP</text>
        {/* CG marker */}
        <circle cx={xAt(cg)} cy={40} r={6} fill="#f59e0b" />
        <text x={xAt(cg)} y={62} textAnchor="middle" fontSize={9}
          fill="#f59e0b" fontFamily="JetBrains Mono">CG</text>
        {/* Static margin arc */}
        <path
          d={`M ${xAt(cp)} 30 L ${xAt(cg)} 30`}
          stroke="#10b981" strokeWidth={1.5} strokeDasharray="3 2"
        />
        <text x={(xAt(cp) + xAt(cg)) / 2} y={22} textAnchor="middle"
          fontSize={9} fill="#10b981" fontFamily="JetBrains Mono">
          static margin {calibers.toFixed(0)} cal
        </text>
        {/* ends */}
        <text x={PAD} y={80} textAnchor="start" fontSize={8} fill="#71717a"
          fontFamily="JetBrains Mono">NOCK</text>
        <text x={VIEW_W - PAD} y={80} textAnchor="end" fontSize={8} fill="#71717a"
          fontFamily="JetBrains Mono">SHAFT END (L = {L.toFixed(1)}")</text>
      </svg>

      <p className="text-[11px] text-zinc-500 mt-3 leading-snug">
        Static margin = (CG − CP) / diameter. Positive means CG is forward of CP (stable). Above 30
        calibers is reliably stable; under 30 indicates marginal flight. Diameter used: {od.toFixed(3)}".
      </p>
    </Card>
  );
}

function Stat({
  label, value, hint, highlight,
}: { label: string; value: string; hint?: string; highlight?: boolean }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-mono text-sm tabular-nums ${highlight ? 'text-ember-500 font-bold' : ''}`}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-zinc-500 font-mono">{hint}</div>}
    </div>
  );
}
